import React, { useState, useMemo } from "react";
import { Button, Alert } from "react-bootstrap";
import {
  IconSearch,
  IconFilter,
} from "@tabler/icons-react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { useStockTransfer, StockTransferProvider } from "./context/StockTransferContext";
import StockTransfersTable from "./components/StockTransfersTable";
import StockTransferDetailsModal from "./components/StockTransferDetailsModal";
import { useAuth } from "hooks/useAuth";
import WorkshopTopbar from "./components/WorkshopTopbar";

export default function StockTransfersPage() {
  return (
    <StockTransferProvider>
      <StockTransfersPageContent />
    </StockTransferProvider>
  );
}

function StockTransfersPageContent() {
  const { user } = useAuth();
  const {
    stockTransfers,
    filterOptions,
    handleSearch,
    handleStatusFilter,
    handleFromWarehouseFilter,
    handleToWarehouseFilter,
    handleDateFromFilter,
    handleDateToFilter,
    Pagination,
  } = useStockTransfer();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [modalShowPrice, setModalShowPrice] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isAdminMekanik = useMemo(() => {
    if (!user) return false;
    
    // Check permission as requested
    const hasPermission = user.permissions?.some(p => p.name === "admin_mekanik");
    // Also check role just in case
    const hasRole = user.roles?.some(r => r.name === "admin_mekanik" || r.name === "superadmin");
    
    return hasPermission || hasRole;
  }, [user]);

  const handleViewDetails = (transfer, showPrice = false) => {
    setSelectedTransfer(transfer);
    setModalShowPrice(showPrice);
    setShowDetailsModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      handleSearch(e);
    }
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-3 workshop-page-scope">
        <WorkshopTopbar />
        <div
          className="workshop-content-panel"
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="h4 h-md-h2 mb-1 text-dark">
                Stock Transfers
              </h2>
              <p className="text-muted small mb-0">
                Kelola dan pantau transfer stok antar warehouse
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <IconSearch size={16} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Cari transfer number atau notes..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyPress={handleSearchSubmit}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <input
                    type="date"
                    className="form-control"
                    placeholder="Date From"
                    value={filterOptions.filters.date_from}
                    onChange={(e) => handleDateFromFilter(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <input
                    type="date"
                    className="form-control"
                    placeholder="Date To"
                    value={filterOptions.filters.date_to}
                    onChange={(e) => handleDateToFilter(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    onClick={() => {
                      // Reset filters
                      handleStatusFilter("all");
                      handleFromWarehouseFilter("");
                      handleToWarehouseFilter("");
                      handleDateFromFilter("");
                      handleDateToFilter("");
                      setSearchTerm("");
                    }}
                  >
                    <IconFilter size={16} className="me-1" />
                    Reset Filter
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {stockTransfers.error && (
            <Alert variant="danger" style={{ marginBottom: "24px" }}>
              <strong>Error loading stock transfers:</strong> {stockTransfers.error}
            </Alert>
          )}

          {/* Stock Transfers Table */}
          <StockTransfersTable
            stockTransfers={stockTransfers}
            onViewDetails={handleViewDetails}
            Pagination={Pagination}
            isAdminMekanik={isAdminMekanik}
          />

          {/* Debug Info */}
          <div className="mt-5">
            <details>
              <summary>Debug Info</summary>
              <pre>{JSON.stringify(stockTransfers, null, 2)}</pre>
            </details>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <StockTransferDetailsModal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setSelectedTransfer(null);
          setModalShowPrice(false);
        }}
        transfer={selectedTransfer}
        showPrice={modalShowPrice}
      />
    </LayoutDashboard>
  );
}
